import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { CreateInviteForm } from '../components/CreateInviteForm';
import { InviteList } from '../components/InviteList';
import { UserManagement } from '../components/UserManagement';
import { useAuth } from '../context/AuthContext';
import AdminDataService from '../services/AdminDataService';
import { hasAdminAccess } from '../utils/roleUtils';
import './AdminPanel.css';

type AdminTab = 'dashboard' | 'users' | 'invites' | 'create-invite' | 'settings';

export const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const { user } = useAuth();
  const [roleCheckError, setRoleCheckError] = useState<string>('');

  // Enhanced role checking with debugging
  const checkAdminAccess = async () => {
    console.log('AdminPanel: Current user:', user);
    console.log('AdminPanel: User role:', user?.role);
    console.log('AdminPanel: Token in localStorage:', localStorage.getItem('token'));
    
    if (!user) {
      setRoleCheckError('No user logged in');
      return false;
    }

    const hasAccess = hasAdminAccess(user.role);
    
    console.log('AdminPanel: Role check result:', {
      userRole: user.role,
      hasAccess
    });

    if (!hasAccess) {
      setRoleCheckError(`Role "${user.role}" is not authorized. Required: Admin or Director`);
      return false;
    }

    // Test API access
    try {
      const adminService = AdminDataService.getInstance();
      await adminService.getUsers(true);
      setRoleCheckError('');
      return true;
    } catch (error: any) {
      console.error('AdminPanel: API access test failed:', error);
      setRoleCheckError(`API Access Failed: ${error.message}`);
      return false;
    }
  };

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  // Check if user has admin privileges (enhanced check)
  const canAccessAdmin = hasAdminAccess(user?.role);

  if (!canAccessAdmin || roleCheckError) {
    return (
      <Layout>
        <div className="admin-panel">
          <div className="access-denied">
            <h2>Admin Panel Access</h2>
            <div style={{ marginBottom: '1rem' }}>
              <h3>Debug Information:</h3>
              <p><strong>User:</strong> {user?.username || 'Not logged in'}</p>
              <p><strong>Current Role:</strong> {user?.role || 'No role'}</p>
              <p><strong>Required Roles:</strong> Admin, Director</p>
              <p><strong>Has Token:</strong> {localStorage.getItem('access_token') ? 'Yes' : 'No'}</p>
              {roleCheckError && (
                <p style={{ color: 'red' }}><strong>Error:</strong> {roleCheckError}</p>
              )}
            </div>
            <button 
              onClick={checkAdminAccess}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '1rem'
              }}
            >
              Retry Access Check
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--text-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'dashboard' as AdminTab, label: 'Dashboard', icon: '📊' },
    { id: 'users' as AdminTab, label: 'User Management', icon: '👥' },
    { id: 'invites' as AdminTab, label: 'View Invites', icon: '📧' },
    { id: 'create-invite' as AdminTab, label: 'Create Invite', icon: '➕' },
    { id: 'settings' as AdminTab, label: 'Settings', icon: '⚙️' },
  ];

  const handleInviteCreated = () => {
    // Switch to invites tab to see the new invite
    setActiveTab('invites');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="tab-content">
            <h2>Admin Dashboard</h2>
            <div className="admin-dashboard">
              <div className="dashboard-welcome">
                <h3>Welcome, {user?.username}!</h3>
                <p>Role: <strong>{user?.role}</strong></p>
                <p>You have full administrative access to the system.</p>
              </div>
              
              <div className="dashboard-quick-actions">
                <h3>Quick Actions</h3>
                <div className="quick-action-buttons">
                  <button 
                    onClick={() => setActiveTab('users')}
                    className="quick-action-btn"
                  >
                    👥 Manage Users
                  </button>
                  <button 
                    onClick={() => setActiveTab('create-invite')}
                    className="quick-action-btn"
                  >
                    ➕ Create Invite
                  </button>
                  <button 
                    onClick={() => setActiveTab('invites')}
                    className="quick-action-btn"
                  >
                    📧 View Invites
                  </button>
                </div>
              </div>

              <div className="dashboard-system-info">
                <h3>System Information</h3>
                <div className="system-info-grid">
                  <div className="info-card">
                    <h4>Authentication</h4>
                    <p>✅ JWT tokens active</p>
                    <p>✅ 2FA enabled</p>
                    <p>✅ Role-based access</p>
                  </div>
                  <div className="info-card">
                    <h4>Security</h4>
                    <p>✅ Password encryption</p>
                    <p>✅ Token blacklisting</p>
                    <p>✅ Session management</p>
                  </div>
                  <div className="info-card">
                    <h4>Email Service</h4>
                    <p>✅ SMTP configured</p>
                    <p>✅ Invite notifications</p>
                    <p>✅ Password resets</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'users':
        return <UserManagement />;
      case 'invites':
        return <InviteList />;
      case 'create-invite':
        return <CreateInviteForm onInviteCreated={handleInviteCreated} />;
      case 'settings':
        return (
          <div className="tab-content">
            <h2>System Settings</h2>
            <p>System configuration coming soon...</p>
            <div className="feature-list">
              <h3>Planned Features:</h3>
              <ul>
                <li>2FA policy settings</li>
                <li>Password requirements</li>
                <li>Session timeout settings</li>
                <li>Email configuration</li>
                <li>System maintenance</li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <p>Manage users, invitations, and system settings</p>
        </div>

        <div className="admin-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="admin-content">
          {renderTabContent()}
        </div>
      </div>
    </Layout>
  );
};